import React from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram } from "lucide-react";

const FOOTER_COLS = {
  "Customer Hub": [
    { label: "Terms & Conditions", to: "/terms" },
    { label: "Shipping & Returns", to: "/shipping-returns" },
    { label: "Track My Order", to: "/track-order" },
    { label: "Privacy Policy", to: "/privacy-policy" },
    { label: "Contact", to: "/contact" },
    { label: "FAQs", to: "/faqs" },
  ],
  Shop: [
    { label: "Face Care", to: "/shop/skincare" },
    { label: "Body Care", to: "/shop/body" },
    { label: "Hair Care", to: "/shop/hair" },
    { label: "Makeup", to: "/shop/makeup" },
    { label: "Fragrance", to: "/shop/fragrance" },
    { label: "Tools & Accessories", to: "/shop/beauty-tools" },
  ],
  Company: [
    { label: "About Doria", to: "/about" },
    { label: "Sustainability", to: "/about#sustainability" },
    { label: "Bestsellers", to: "/shop/bestsellers" },
  ],
};

export default function Footer() {
  return (
    <footer className="bg-[#FBF7F3] border-t border-[#333333]/08" data-testid="site-footer">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12 py-16 lg:py-20">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-10 lg:gap-16">
          {/* Customer Hub */}
          <div>
            <p className="text-[11px] tracking-[0.2em] uppercase font-sans font-medium text-[#333333] mb-5">Customer Hub</p>
            <ul className="space-y-3">
              {FOOTER_COLS["Customer Hub"].map((item) => (
                <li key={item.label}><Link to={item.to} data-testid={`footer-link-${item.label.toLowerCase().replace(/&/g, "and").replace(/[^a-z]+/g, "-")}`} className="font-sans text-sm text-[#333333]/75 hover:text-[#333333] transition-colors">{item.label}</Link></li>
              ))}
            </ul>
          </div>

          {/* Shop */}
          <div>
            <p className="text-[11px] tracking-[0.2em] uppercase font-sans font-medium text-[#333333] mb-5">Shop</p>
            <ul className="space-y-3">
              {FOOTER_COLS.Shop.map((item) => (
                <li key={item.label}><Link to={item.to} data-testid={`footer-link-${item.label.toLowerCase().replace(/&/g, "and").replace(/[^a-z]+/g, "-")}`} className="font-sans text-sm text-[#333333]/75 hover:text-[#333333] transition-colors">{item.label}</Link></li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <p className="text-[11px] tracking-[0.2em] uppercase font-sans font-medium text-[#333333] mb-5">Company</p>
            <ul className="space-y-3">
              {FOOTER_COLS.Company.map((item) => (
                <li key={item.label}><Link to={item.to} data-testid={`footer-link-${item.label.toLowerCase().replace(/&/g, "and").replace(/[^a-z]+/g, "-")}`} className="font-sans text-sm text-[#333333]/75 hover:text-[#333333] transition-colors">{item.label}</Link></li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <p className="text-[11px] tracking-[0.2em] uppercase font-sans font-medium text-[#333333] mb-5">Contact</p>
            <ul className="space-y-3 font-sans text-sm text-[#333333]/75">
              <li><a href="mailto:doriaofficial1@gmail.com" className="hover:text-[#333333] transition-colors" data-testid="footer-email">doriaofficial1@gmail.com</a></li>
              <li>Mon - Fri, 9am - 6pm EST</li>
              <li><Link to="/contact" className="underline underline-offset-4 hover:text-[#333333] transition-colors" data-testid="footer-contact-link">Send us a message</Link></li>
            </ul>
            <div className="flex items-center gap-3 mt-5">
              <a href="https://facebook.com/officialdoriabeauty" target="_blank" rel="noopener noreferrer" aria-label="Facebook" data-testid="footer-facebook" className="w-12 h-12 flex items-center justify-center border border-[#333333]/15 rounded-xl hover:border-[#333333]/40 transition-colors">
                <Facebook className="w-5 h-5 text-[#333333]/75" />
              </a>
              <a href="https://instagram.com/doriaofficial1" target="_blank" rel="noopener noreferrer" aria-label="Instagram" data-testid="footer-instagram" className="w-12 h-12 flex items-center justify-center border border-[#333333]/15 rounded-xl hover:border-[#333333]/40 transition-colors">
                <Instagram className="w-5 h-5 text-[#333333]/75" />
              </a>
            </div>
          </div>
        </div>

        {/* Giant DORIA wordmark with image mask */}
        <div className="mt-20 mb-10 text-center relative overflow-hidden">
          <span
            className="font-serif text-[80px] sm:text-[120px] lg:text-[180px] font-bold leading-none tracking-tight select-none"
            style={{
              backgroundImage: "url(https://customer-assets-0z36b82j.emergentagent.net/job_81ce4017-7590-49ec-976b-7c0a9c57511a/artifacts/pseilzwp_face2.webp)",
              backgroundSize: "cover",
              backgroundPosition: "center",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              color: "transparent",
            }}
          >DORIA</span>
        </div>

        {/* Copyright + payment icons */}
        <div className="border-t border-[#333333]/08 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="font-sans text-xs sm:text-[13px] text-[#333333]/75" data-testid="footer-copyright">Copyright &copy; {new Date().getFullYear()}.</p>
          <div className="flex items-center gap-2">
            <span className="bg-[#1A1F71] text-white text-[10px] font-bold rounded px-2.5 py-1.5">VISA</span>
            <span className="bg-[#EB001B] text-white text-[10px] font-bold rounded px-2.5 py-1.5">MC</span>
            <span className="bg-[#006FCF] text-white text-[10px] font-bold rounded px-2.5 py-1.5">AMEX</span>
            <span className="bg-[#003087] text-white text-[10px] font-bold rounded px-2.5 py-1.5">PayPal</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
